import { IconButton, Box, Chip, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Dialog, Avatar } from "@mui/material";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import LogoutIcon from '@mui/icons-material/Logout';
import React, { useState, useEffect } from "react";
import { AppBar, useLogout } from "react-admin";
import config from "../config";
import StorePicker from "./StorePicker";

/** backend.html's accent orange (--orange / --orange-2). */
const ACCOUNT_ORANGE = '#ff6a3d';
const ACCOUNT_ORANGE_DEEP = '#e8521f';
import { MfaSettings } from "./MfaSettings";
import { ProfileSettings } from "./ProfileSettings";
import { useThemeMode } from "../context/ThemeModeContext";
import { nextGround } from "../theme/grounds";
import { usePermissions } from "../context/PermissionContext";
import { decodeToken, getStoredToken, getValidToken } from "../services/tokenRefreshService";
import Logger from "../utils/Logger";

/**
 * CustomAppBar Component
 *
 * Top navigation bar with red gradient background matching mobile app design.
 * Contains:
 * - the ground picker (one button, cycles the six grounds — see theme/grounds.ts)
 * - the account menu
 *
 * The bar itself is Deeliver blue on every ground: it is brand, not surface,
 * so it does not re-tint with the picker.
 *
 * Note: Database sync has been moved to the Sync Control page
 * under Admin Functions menu for better organization.
 * Refresh button is disabled - use Sync Control page instead.
 */
function CustomAppBar() {
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const [isMfaSettingsOpen, setIsMfaSettingsOpen] = useState(false);
  const [isProfileSettingsOpen, setIsProfileSettingsOpen] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<{
    avatarUrl: string | null;
    displayName: string | null;
    firstName: string | null;
    lastName: string | null;
  } | null>(null);
  const [storeIds, setStoreIds] = useState<string[]>([]);
  const { ground, cycleGround } = useThemeMode();
  const { role } = usePermissions();
  const logout = useLogout();

  // Get user email from token
  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      const decoded = decodeToken(token);
      if (decoded?.email) {
        setUserEmail(decoded.email);
      }
    }
  }, []);

  // Force profile refresh
  const [profileVersion, setProfileVersion] = useState(0);
  const handleProfileUpdate = () => {
    setProfileVersion(v => v + 1);
  };

  // Fetch user profile (avatar, display name)
  useEffect(() => {
    const fetchProfile = async () => {
      const token = await getValidToken();
      if (!token) return;

      try {
        const response = await fetch(`${config.API_URL}/admin/users/profile/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setUserProfile({
            avatarUrl: data.profile?.avatarUrl || null,
            displayName: data.profile?.displayName || null,
            firstName: data.profile?.firstName || null,
            lastName: data.profile?.lastName || null,
          });
        }
      } catch (error) {
        Logger.error('Failed to fetch user profile:', error);
      }
    };

    fetchProfile();
  }, [isProfileSettingsOpen, profileVersion]); // Refetch when profile dialog closes or when version changes



  // Fetch store IDs for alert bell
  useEffect(() => {
    const fetchStores = async () => {
      const token = await getValidToken();
      if (!token) return;

      try {
        const response = await fetch(`${config.API_URL}/admin/stores`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          // Extract store external IDs (used for alerts)
          const ids = (data.data || []).map((store: { externalId: string }) => store.externalId);
          setStoreIds(ids);
        }
      } catch (error) {
        Logger.error('Failed to fetch stores for alerts:', error);
      }
    };

    fetchStores();
  }, []);

  // Account menu handlers
  const handleAccountMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget);
  };
  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };
  const handleMfaSettingsOpen = () => {
    handleAccountMenuClose();
    setIsMfaSettingsOpen(true);
  };
  const handleMfaSettingsClose = () => {
    setIsMfaSettingsOpen(false);
  };
  const handleProfileSettingsOpen = () => {
    handleAccountMenuClose();
    setIsProfileSettingsOpen(true);
  };
  const handleProfileSettingsClose = () => {
    setIsProfileSettingsOpen(false);
  };
  const handleLogout = () => {
    handleAccountMenuClose();
    logout();
  };

  // Get avatar initial for fallback
  const getAvatarInitial = () => {
    if (userProfile?.displayName) {
      return userProfile.displayName.charAt(0).toUpperCase();
    }
    if (userProfile?.firstName) {
      return userProfile.firstName.charAt(0).toUpperCase();
    }
    if (userEmail) {
      return userEmail.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    // alwaysOn disables react-admin's default HideOnScroll wrapper so the header
    // stays pinned (fixed) instead of sliding away when the page scrolls.
    // No burger: the rail never collapses to icons (the reskin prototype has
    // none, removed 2026-09-12). react-admin renders its toggle unconditionally,
    // so it is hidden here and CustomMenu pins the sidebar open.
    <AppBar
      userMenu={false}
      alwaysOn
      sx={{
        '& .RaAppBar-menuButton': { display: 'none' },
        // react-admin's own refresh (LoadingIndicator) inherits a muted colour
        // and vanished on the blue bar; paint it like the ground button beside it.
        '& .RaLoadingIndicator-loadedIcon': {
          color: 'rgba(255,255,255,0.85)',
          '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.16)' },
        },
        '& .RaLoadingIndicator-loader': { color: '#fff' },
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          height: 56,
          px: 2,
        }}
      >
        {/* Left side - Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/deeliver-white.png"
            alt="Deeliver"
            onClick={() => window.location.reload()}
            // deeliver-white.png is the wordmark + dots with no slogan (2000x500);
            // 26px tall keeps the wordmark itself the size it was under the
            // 34px slogan version.
            sx={{
              height: 26,
              width: 'auto',
              display: 'block',
              cursor: 'pointer',
            }}
          />
          {/* No 'Deeliver' text — the white wordmark already says it. Just the
              'Admin Panel' label, vertically centred beside the logo. */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
            <Box
              sx={{
                fontFamily: 'var(--f-data)',
                fontSize: '0.6875rem',
                fontWeight: 500,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.78)',
                borderLeft: '1px solid rgba(255,255,255,0.24)',
                pl: 1.75,
                ml: 0.25,
                lineHeight: 1,
              }}
            >
              Admin Panel
            </Box>
          </Box>
        </Box>

        {/* Centre — branch picker. Renders only on store-scoped pages (Live
            Orders), so it isn't sitting there implying a filter that does nothing.
            Absolutely positioned so it's centred on the BAR, not on whatever space
            is left between the logo and the actions. */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: { xs: 'none', md: 'block' },
          }}
        >
          <StorePicker />
        </Box>

        {/* Right side - Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {/* Role Badge - Only show if user has staff role */}
          {role && (
            <Chip
              icon={<AdminPanelSettingsIcon />}
              label={role.charAt(0).toUpperCase() + role.slice(1)}
              size="small"
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.26)',
                backgroundColor: 'rgba(255,255,255,0.16)',
                '& .MuiChip-icon': { color: 'white', fontSize: 14 },
                fontFamily: 'var(--f-data)',
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: '0.11em',
                textTransform: 'uppercase',
                height: 26,
              }}
              variant="outlined"
            />
          )}

          {/* Ground picker — one button, cycles: Deep ink → Cool slate → Storm
              blue → Mulberry → Mist → Bone → back. The tooltip names where the
              next press lands, since six is too many to cycle blind. */}
          <Tooltip title={`${ground.name} · next: ${nextGround(ground.id).name}`}>
            <IconButton
              onClick={cycleGround}
              size="small"
              aria-label={`Ground: ${ground.name}. Switch to ${nextGround(ground.id).name}`}
              sx={{
                color: 'rgba(255,255,255,0.8)',
                backgroundColor: 'transparent',
                border: '1px solid transparent',
                '&:hover': {
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.16)',
                  borderColor: 'rgba(255,255,255,0.24)',
                },
              }}
            >
              <WbSunnyOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>

          {/* Account Menu Button with User Avatar */}
          <Tooltip title="Account Settings">
            {/* The button is invisible chrome around the avatar — no border of
                its own (the theme's 6px IconButton radius made it a square
                frame round a circle) — so the avatar's one hairline ring is the
                whole outline, as on the prototype's user dot. */}
            <IconButton
              onClick={handleAccountMenuOpen}
              size="small"
              sx={{
                p: 0.25,
                borderRadius: '50%',
                '& .MuiAvatar-root': { transition: 'border-color .15s' },
                '&:hover .MuiAvatar-root': { borderColor: 'rgba(255,255,255,0.75)' },
              }}
            >
              <Avatar
                src={userProfile?.avatarUrl || undefined}
                sx={{
                  width: 32,
                  height: 32,
                  background: `linear-gradient(140deg, ${ACCOUNT_ORANGE}, ${ACCOUNT_ORANGE_DEEP})`,
                  color: '#fff',
                  fontSize: '0.875rem',
                  fontWeight: 800,
                  border: '1px solid rgba(255,255,255,0.4)',
                }}
              >
                {!userProfile?.avatarUrl && getAvatarInitial()}
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* Account Dropdown Menu */}
          <Menu
            anchorEl={accountMenuAnchor}
            open={Boolean(accountMenuAnchor)}
            onClose={handleAccountMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 220,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }
            }}
          >
            {userEmail && (
              <MenuItem disabled sx={{ opacity: '1 !important', py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 48 }}>
                  <Avatar
                    src={userProfile?.avatarUrl || undefined}
                    sx={{ width: 36, height: 36 }}
                  >
                    {!userProfile?.avatarUrl && getAvatarInitial()}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={userProfile?.displayName || `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() || userEmail}
                  secondary={userEmail}
                  primaryTypographyProps={{ fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleProfileSettingsOpen}>
              <ListItemIcon>
                <PersonIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Profile Settings" secondary="Display name & avatar" />
            </MenuItem>
            <MenuItem onClick={handleMfaSettingsOpen}>
              <ListItemIcon>
                <SecurityIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="MFA Settings" secondary="Two-factor authentication" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>

          {/* Profile Settings Dialog */}
          <Dialog
            open={isProfileSettingsOpen}
            onClose={handleProfileSettingsClose}
            maxWidth="sm"
            fullWidth
          >
            <ProfileSettings onProfileUpdate={handleProfileUpdate} />
          </Dialog>

          {/* MFA Settings Dialog */}
          <Dialog
            open={isMfaSettingsOpen}
            onClose={handleMfaSettingsClose}
            maxWidth="sm"
            fullWidth
          >
            <MfaSettings />
          </Dialog>
        </Box>
      </Box>
    </AppBar>
  );
}

export default CustomAppBar;
